import { 
  Droplet, 
  Wind, 
  Leaf, 
  Zap, 
  Wrench, 
  Hammer, 
  Truck, 
  Building2, 
  Scissors, 
  Utensils, 
  ShoppingBag, 
  Heart, 
  GraduationCap, 
  Briefcase, 
  Factory,
  Trees,
  Paintbrush,
  Laptop,
  Stethoscope,
  Scale,
  Camera,
  Music,
  Car,
  Plane,
  Package,
  Home,
  DollarSign,
  Users,
  Globe,
  Sparkles,
  Waves,
  Droplets
} from 'lucide-react';

/**
 * Reverse mapping from component to icon name
 * This is critical for serialization to database
 */
const componentToNameMap = new Map([
  [Droplet, 'Droplet'],
  [Wind, 'Wind'],
  [Leaf, 'Leaf'],
  [Zap, 'Zap'],
  [Wrench, 'Wrench'],
  [Hammer, 'Hammer'],
  [Truck, 'Truck'],
  [Building2, 'Building2'],
  [Scissors, 'Scissors'],
  [Utensils, 'Utensils'],
  [ShoppingBag, 'ShoppingBag'],
  [Heart, 'Heart'],
  [GraduationCap, 'GraduationCap'],
  [Briefcase, 'Briefcase'],
  [Factory, 'Factory'],
  [Trees, 'Trees'],
  [Paintbrush, 'Paintbrush'],
  [Laptop, 'Laptop'],
  [Stethoscope, 'Stethoscope'],
  [Scale, 'Scale'],
  [Camera, 'Camera'],
  [Music, 'Music'],
  [Car, 'Car'],
  [Plane, 'Plane'],
  [Package, 'Package'],
  [Home, 'Home'],
  [DollarSign, 'DollarSign'],
  [Users, 'Users'],
  [Globe, 'Globe'],
  [Sparkles, 'Sparkles'],
  [Waves, 'Waves'],
  [Droplets, 'Droplets']
]);

/**
 * Get icon name from component for database serialization
 */
export const getIconName = (iconComponent) => {
  return componentToNameMap?.get(iconComponent) || 'Circle';
};

/**
 * Industry-specific confetti icon mapping based on NAICS codes
 * Each industry gets themed icons that represent their business niche
 */
export const industryConfettiMap = {
  // Construction (23)
  '23': [
    { icon: Hammer, color: '#f59e0b', label: 'Hammer' },
    { icon: Wrench, color: '#ef4444', label: 'Wrench' },
    { icon: Building2, color: '#6b7280', label: 'Building' },
    { icon: Truck, color: '#3b82f6', label: 'Truck' }
  ],
  
  // HVAC & Plumbing (238220, 238)
  '238220': [
    { icon: Wind, color: '#60a5fa', label: 'Wind' },
    { icon: Droplet, color: '#3b82f6', label: 'Water Drop' },
    { icon: Wrench, color: '#ef4444', label: 'Wrench' },
    { icon: Wind, color: '#93c5fd', label: 'Air' }
  ],
  '238': [
    { icon: Wind, color: '#60a5fa', label: 'Wind' },
    { icon: Droplet, color: '#3b82f6', label: 'Water Drop' },
    { icon: Wrench, color: '#ef4444', label: 'Wrench' }
  ],
  
  // Landscaping (561730)
  '561730': [
    { icon: Leaf, color: '#10b981', label: 'Leaf' },
    { icon: Trees, color: '#059669', label: 'Tree' },
    { icon: Leaf, color: '#34d399', label: 'Leaf' },
    { icon: Scissors, color: '#6b7280', label: 'Scissors' }
  ],
  
  // Electrical (238210)
  '238210': [
    { icon: Zap, color: '#fbbf24', label: 'Lightning' },
    { icon: Zap, color: '#f59e0b', label: 'Bolt' },
    { icon: Wrench, color: '#ef4444', label: 'Wrench' },
    { icon: Zap, color: '#fcd34d', label: 'Electric' }
  ],
  
  // Roofing (238160)
  '238160': [
    { icon: Home, color: '#6b7280', label: 'House' },
    { icon: Hammer, color: '#f59e0b', label: 'Hammer' },
    { icon: Building2, color: '#3b82f6', label: 'Building' },
    { icon: Wrench, color: '#ef4444', label: 'Tool' }
  ],
  
  // Painting (238320)
  '238320': [
    { icon: Paintbrush, color: '#ec4899', label: 'Paintbrush' },
    { icon: Paintbrush, color: '#8b5cf6', label: 'Brush' },
    { icon: Paintbrush, color: '#3b82f6', label: 'Paint' },
    { icon: Sparkles, color: '#f59e0b', label: 'Sparkle' }
  ],
  
  // Restaurants & Food Services (722)
  '722': [
    { icon: Utensils, color: '#ef4444', label: 'Utensils' },
    { icon: Utensils, color: '#f59e0b', label: 'Fork Knife' },
    { icon: Heart, color: '#ec4899', label: 'Heart' },
    { icon: Sparkles, color: '#fbbf24', label: 'Star' }
  ],
  
  // Retail (44, 45)
  '44': [
    { icon: ShoppingBag, color: '#8b5cf6', label: 'Shopping Bag' },
    { icon: Package, color: '#3b82f6', label: 'Package' },
    { icon: DollarSign, color: '#10b981', label: 'Dollar' },
    { icon: Sparkles, color: '#f59e0b', label: 'Sparkle' }
  ],
  '45': [
    { icon: ShoppingBag, color: '#8b5cf6', label: 'Shopping Bag' },
    { icon: Package, color: '#3b82f6', label: 'Package' },
    { icon: DollarSign, color: '#10b981', label: 'Dollar' }
  ],
  
  // Healthcare (62)
  '62': [
    { icon: Heart, color: '#ef4444', label: 'Heart' },
    { icon: Stethoscope, color: '#3b82f6', label: 'Stethoscope' },
    { icon: Heart, color: '#ec4899', label: 'Health' },
    { icon: Sparkles, color: '#10b981', label: 'Care' }
  ],
  
  // Education (61)
  '61': [
    { icon: GraduationCap, color: '#3b82f6', label: 'Graduation Cap' },
    { icon: GraduationCap, color: '#8b5cf6', label: 'Education' },
    { icon: Sparkles, color: '#f59e0b', label: 'Star' },
    { icon: Briefcase, color: '#6b7280', label: 'Learning' }
  ],
  
  // Professional Services (54)
  '54': [
    { icon: Briefcase, color: '#3b82f6', label: 'Briefcase' },
    { icon: Laptop, color: '#6b7280', label: 'Laptop' },
    { icon: Users, color: '#8b5cf6', label: 'Team' },
    { icon: Sparkles, color: '#f59e0b', label: 'Excellence' }
  ],
  
  // Legal Services (5411)
  '5411': [
    { icon: Scale, color: '#3b82f6', label: 'Justice Scale' },
    { icon: Briefcase, color: '#6b7280', label: 'Briefcase' },
    { icon: Scale, color: '#8b5cf6', label: 'Law' },
    { icon: Sparkles, color: '#f59e0b', label: 'Excellence' }
  ],
  
  // Manufacturing (31, 32, 33)
  '31': [
    { icon: Factory, color: '#6b7280', label: 'Factory' },
    { icon: Package, color: '#3b82f6', label: 'Product' },
    { icon: Wrench, color: '#ef4444', label: 'Tool' },
    { icon: Sparkles, color: '#f59e0b', label: 'Innovation' }
  ],
  '32': [
    { icon: Factory, color: '#6b7280', label: 'Factory' },
    { icon: Package, color: '#3b82f6', label: 'Product' },
    { icon: Wrench, color: '#ef4444', label: 'Tool' }
  ],
  '33': [
    { icon: Factory, color: '#6b7280', label: 'Factory' },
    { icon: Package, color: '#3b82f6', label: 'Product' },
    { icon: Wrench, color: '#ef4444', label: 'Tool' }
  ],
  
  // Transportation (48, 49)
  '48': [
    { icon: Truck, color: '#3b82f6', label: 'Truck' },
    { icon: Plane, color: '#6b7280', label: 'Plane' },
    { icon: Car, color: '#ef4444', label: 'Car' },
    { icon: Package, color: '#f59e0b', label: 'Package' }
  ],
  '49': [
    { icon: Truck, color: '#3b82f6', label: 'Truck' },
    { icon: Package, color: '#f59e0b', label: 'Package' },
    { icon: Globe, color: '#10b981', label: 'Global' }
  ],
  
  // Arts & Entertainment (71)
  '71': [
    { icon: Music, color: '#ec4899', label: 'Music' },
    { icon: Camera, color: '#8b5cf6', label: 'Camera' },
    { icon: Sparkles, color: '#f59e0b', label: 'Star' },
    { icon: Heart, color: '#ef4444', label: 'Love' }
  ],
  
  // Real Estate (53)
  '53': [
    { icon: Home, color: '#3b82f6', label: 'Home' },
    { icon: Building2, color: '#6b7280', label: 'Building' },
    { icon: DollarSign, color: '#10b981', label: 'Value' },
    { icon: Sparkles, color: '#f59e0b', label: 'Dream' }
  ],
  
  // Appliance Repair (811412)
  '811412': [
    { icon: Wrench, color: '#ef4444', label: 'Wrench' },
    { icon: Wrench, color: '#f59e0b', label: 'Tool' },
    { icon: Zap, color: '#fbbf24', label: 'Electric' },
    { icon: Wrench, color: '#dc2626', label: 'Repair' }
  ],
  
  // Barber Shops (812111)
  '812111': [
    { icon: Scissors, color: '#ef4444', label: 'Scissors' },
    { icon: Scissors, color: '#3b82f6', label: 'Barber Pole' },
    { icon: Scissors, color: '#dc2626', label: 'Cut' },
    { icon: Sparkles, color: '#f59e0b', label: 'Style' }
  ],
  
  // Beauty Salons (812112)
  '812112': [
    { icon: Scissors, color: '#ec4899', label: 'Scissors' },
    { icon: Scissors, color: '#8b5cf6', label: 'Style' },
    { icon: Sparkles, color: '#f59e0b', label: 'Glamour' },
    { icon: Heart, color: '#ef4444', label: 'Beauty' }
  ],
  
  // Auto Detailing (811192)
  '811192': [
    { icon: Droplets, color: '#3b82f6', label: 'Soap Suds' },
    { icon: Waves, color: '#60a5fa', label: 'Water' },
    { icon: Car, color: '#6b7280', label: 'Car' },
    { icon: Sparkles, color: '#93c5fd', label: 'Shine' }
  ],
  
  // Floor Cleaning / Janitorial (561720)
  '561720': [
    { icon: Droplet, color: '#3b82f6', label: 'Mop' },
    { icon: Droplets, color: '#60a5fa', label: 'Bucket' },
    { icon: Droplet, color: '#6b7280', label: 'Clean' },
    { icon: Sparkles, color: '#10b981', label: 'Fresh' }
  ],
  
  // Default fallback for unmatched industries
  'default': [
    { icon: Sparkles, color: '#3b82f6', label: 'Star' },
    { icon: Sparkles, color: '#8b5cf6', label: 'Sparkle' },
    { icon: Sparkles, color: '#ec4899', label: 'Shine' },
    { icon: Sparkles, color: '#f59e0b', label: 'Glow' }
  ]
};

/**
 * Get confetti icons for a specific NAICS code
 * Matches by exact code first, then by prefix (e.g., 238220 -> 238 -> 23)
 */
export const getIndustryConfetti = (naicsCode) => {
  if (!naicsCode) return industryConfettiMap?.default;
  
  const code = naicsCode?.toString();
  
  // Try exact match
  if (industryConfettiMap?.[code]) {
    return industryConfettiMap?.[code];
  }
  
  // Try progressive prefix matching (238220 -> 2382 -> 238 -> 23)
  for (let i = code?.length - 1; i >= 2; i--) {
    const prefix = code?.substring(0, i);
    if (industryConfettiMap?.[prefix]) {
      return industryConfettiMap?.[prefix];
    }
  }
  
  // Return default if no match
  return industryConfettiMap?.default;
};

/**
 * Get a preview icon for display in tables (returns first icon from the set)
 */
export const getIndustryConfettiPreview = (naicsCode) => {
  const confettiSet = getIndustryConfetti(naicsCode);
  return confettiSet?.[0]; // Return first icon as preview
};

/**
 * Get all available confetti icons for selection
 */
export const getAllConfettiIcons = () => {
  const allIcons = new Map();
  
  Object.values(industryConfettiMap)?.forEach(iconSet => {
    iconSet?.forEach(iconData => {
      const key = `${iconData?.label}-${iconData?.color}`;
      if (!allIcons?.has(key)) {
        allIcons?.set(key, iconData);
      }
    });
  });
  
  return Array.from(allIcons?.values());
};